import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../contexts';
import { useDebounce } from '../../hooks';
import { DELETE_HOURLY_ACCOUNT_USER_GROUP_V2, LIST_HOURLY_ACCOUNT_USER_GROUPS_V2 } from '../../queries';
import { HourlyAccount } from '../../types';
import { datagridFilterPanelAttributes } from '../../utils/cypress';
import { AuditHistoryModal } from '../audit/auditHistoryModal';
import { DatagridLinearProgress } from '../loading/datagridLinearProgress';
import { ConfirmationInputModal } from '../modals/confirmationInputModal';
import { PortalUserGroupDetails } from './portalUserGroupDetails';

const defaultSortOrder = {
	sort: 'desc',
	field: 'pid',
};

interface PortalUserGroupsToolbarProps {
	hourlyAccount?: HourlyAccount;
	hourlyUserGroupsRefetch: () => void;
}

const PortalUserGroupsToolbar = ({ hourlyAccount, hourlyUserGroupsRefetch }: PortalUserGroupsToolbarProps) => {
	const { setShowDialog } = useContext(CommonContext);

	const onCreateAccount = useCallback(() => {
		const dialog = <PortalUserGroupDetails hourlyAccount={hourlyAccount} afterSave={hourlyUserGroupsRefetch} />;
		setShowDialog(dialog);
	}, [hourlyAccount, hourlyUserGroupsRefetch, setShowDialog]);

	return (
		<GridToolbarContainer>
			<GridToolbarFilterButton data-cy='filter-table-button' />
			<GridToolbarColumnsButton />
			<GridToolbarDensitySelector />
			<Button size='small' onClick={onCreateAccount}>
				<AddIcon fontSize='small' />
				New Portal User Group
			</Button>
		</GridToolbarContainer>
	);
};

interface PortalUserGroupsTableProps {
	hourlyAccount?: HourlyAccount;
}

export const PortalUserGroupsTable = ({ hourlyAccount }: PortalUserGroupsTableProps) => {
	const { apolloHeaders, setShowDialog, setShowSnackbar, organizationId } = useContext(CommonContext);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState<number>(0);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [sortOrder, setSortOrder] = useState(defaultSortOrder);
	const [filters, setFilters] = useState([]);
	const debouncedSetFilters = useDebounce(setFilters, 500);

	const {
		data: hourlyUserGroupsData,
		loading: hourlyUserGroupsLoading,
		refetch: hourlyUserGroupsRefetch,
	} = useQuery(LIST_HOURLY_ACCOUNT_USER_GROUPS_V2, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			filters,
			order: sortOrder,
			organizationId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
		fetchPolicy: 'network-only',
		ssr: false,
	});

	const [deleteHourlyAccountUserGroup] = useMutation(DELETE_HOURLY_ACCOUNT_USER_GROUP_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	useEffect(() => {
		if (!loading && hourlyUserGroupsData) {
			setRows(hourlyUserGroupsData?.listHourlyAccountUserGroups?.hourlyAccountUserGroups);
			setRowCount(hourlyUserGroupsData?.listHourlyAccountUserGroups?.count);
		}
	}, [loading, hourlyUserGroupsData]);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			debouncedSetFilters(
				filterModel.items?.map((filterItem) => ({
					propertyName: filterItem.field,
					op: filterItem.operator,
					value: filterItem.value,
				})),
			);
		},
		[debouncedSetFilters],
	);

	const onSortModelChange = useCallback(
		(sortModel: GridSortModel) => {
			if (!sortModel.length) {
				return setSortOrder(defaultSortOrder);
			}

			const { field, sort } = sortModel[0];
			return setSortOrder({ field, sort });
		},
		[setSortOrder],
	);

	const handleEditAccountUserGroup = useCallback(
		(accountUserGroup) => {
			const dialog = (
				<PortalUserGroupDetails accountUserGroupId={accountUserGroup.id} afterSave={hourlyUserGroupsRefetch} />
			);

			setShowDialog(dialog);
		},
		[hourlyUserGroupsRefetch, setShowDialog],
	);

	const handleDeleteAccountUserGroup = useCallback(
		(accountUserGroup) => {
			const confirmationInputModal = (
				<ConfirmationInputModal
					title={`Are you sure you want to delete the user group ${accountUserGroup.userGroup.name}?`}
					validationString='DELETE'
					dismissText='Cancel'
					confirmText='Confirm'
					handleDismiss={() => setShowDialog(null)}
					handleConfirm={() =>
						deleteHourlyAccountUserGroup({
							variables: {
								id: accountUserGroup.id,
							},
							onCompleted: () => {
								setShowDialog(null);
								setShowSnackbar({
									message: `Hourly account user group deleted successfully`,
									severity: 'success',
								});
								hourlyUserGroupsRefetch();
							},
							onError: (apolloError) => {
								setShowSnackbar({
									message: apolloError.message,
									severity: 'error',
								});
							},
						})
					}
				/>
			);
			setShowDialog(confirmationInputModal);
		},
		[deleteHourlyAccountUserGroup, hourlyUserGroupsRefetch, setShowDialog, setShowSnackbar],
	);

	const handleAuditHistory = useCallback(
		(userGroup) => {
			const dialog = (
				<AuditHistoryModal
					title='Hourly Portal User Group Audit History'
					resourceId={String(userGroup.id)}
					resourceType={ResourceTypes.HOURLY_ACCOUNT_USER_GROUP}
					onClose={() => setShowDialog(null)}
				/>
			);

			setShowDialog(dialog);
		},
		[setShowDialog],
	);

	const columns: GridColDef<any>[] = [
		{
			field: 'actions',
			headerName: 'Actions',
			width: 150,
			sortable: false,
			filterable: false,
			renderCell: ({ row }) => {
				const isPrimaryUserGroup = row.masterPolicy;

				const actions = (
					<>
						{!isPrimaryUserGroup && (
							<>
								<IconButton onClick={() => handleEditAccountUserGroup(row)}>
									<EditIcon />
								</IconButton>
								<IconButton onClick={() => handleDeleteAccountUserGroup(row)}>
									<DeleteIcon />
								</IconButton>
							</>
						)}
						<IconButton onClick={() => handleAuditHistory(row)}>
							<HistoryIcon />
						</IconButton>
					</>
				);

				return actions;
			},
		},
		{
			field: 'pid',
			headerName: 'Portal Account ID',
			width: 150,
			type: 'number',
			align: 'left',
			headerAlign: 'left',
			valueGetter: ({ row }) => `PID-${row.hourlyAccount.pid}`,
		},
		{
			field: 'accountName',
			headerName: 'Portal Account Name',
			width: 300,
			valueGetter: ({ row }) => row.hourlyAccount.name,
			sortable: false,
			filterable: false,
		},
		{
			field: 'name',
			headerName: 'Portal User Group',
			width: 150,
			valueGetter: ({ row }) => row.userGroup.name,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility #',
			width: 150,
			valueGetter: ({ row }) => row.hourlyAccount.facility.facilityNumber,
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			width: 300,
			valueGetter: ({ row }) => row.hourlyAccount.facility.facilityName,
		},
		{
			field: 'organization',
			headerName: 'Organization',
			width: 150,
			filterable: false,
			sortable: false,
			valueGetter: ({ row }) => row.hourlyAccount.organizationSettings.name,
		},
	];

	return (
		<div className='pca__datagrid-sticky--headfoot' data-cy='portals-datagrid-container'>
			<DataGridPro
				sx={{ width: 'auto', height: '100%', minHeight: '200px' }}
				rows={rows || []}
				rowCount={rowCount}
				columns={columns}
				loading={hourlyUserGroupsLoading}
				pagination
				pageSizeOptions={[pageSize]}
				paginationMode='server'
				paginationModel={{ page, pageSize }}
				onPaginationModelChange={(newModel) => {
					setLoading(true);
					setPaginationModel(newModel);
				}}
				sortingMode='server'
				onSortModelChange={onSortModelChange}
				filterMode='server'
				onFilterModelChange={onFilterModelChange}
				slots={{
					toolbar: PortalUserGroupsToolbar,
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{
					toolbar: {
						hourlyAccount,
						hourlyUserGroupsRefetch,
					},
					filterPanel: {
						sx: {
							'& .MuiDataGrid-filterFormValueInput': { width: 240 },
						},
						...datagridFilterPanelAttributes,
					},
				}}
			/>
		</div>
	);
};
